import React from 'react';
import { Link } from 'react-router-dom';
import { HeroSection } from '../../components/HeroSection';
import { Building2, Stethoscope, Briefcase, Shirt, Monitor, Heart, Bus, GraduationCap, Scale, ArrowRight, AlertTriangle, BookOpen } from 'lucide-react';
import { motion } from 'framer-motion';
interface AccessCategory {
  icon: React.ReactNode;
  title: string;
  description: string;
  examples: string[];
  color: string;
}
const categories: AccessCategory[] = [{
  icon: <Building2 className="h-8 w-8" data-id="element-746" />,
  title: 'Physical Space & Built Environment',
  description: 'The design of public and private spaces — seating, doorways, aisles, turnstiles, bathrooms — and whether they accommodate diverse body sizes.',
  examples: ['Fixed-width seating in theatres, stadiums, and restaurants', 'Narrow turnstiles and doorways in public transit', 'Bathroom stalls too small for larger bodies', 'Waiting room chairs with armrests that exclude'],
  color: 'border-brand-magenta'
}, {
  icon: <Stethoscope className="h-8 w-8" data-id="element-747" />,
  title: 'Healthcare & Medical Access',
  description: 'The ability to receive dignified, evidence-based medical care without weight-based discrimination, diagnostic overshadowing, or refusal of treatment.',
  examples: ['Blood pressure cuffs and gowns that fit', 'Medical equipment with adequate weight limits', 'Providers who diagnose beyond "lose weight"', 'Access to surgery and treatment without BMI gatekeeping'],
  color: 'border-brand-cyan'
}, {
  icon: <Briefcase className="h-8 w-8" data-id="element-748" />,
  title: 'Employment & Economic Access',
  description: 'Fair treatment in hiring, workplace culture, advancement, and economic participation regardless of body size.',
  examples: ['Hiring discrimination based on appearance', 'Workplace furniture that accommodates all sizes', 'Dress codes that assume thin bodies', 'Wage penalties correlated with body size'],
  color: 'border-brand-lime'
}, {
  icon: <Shirt className="h-8 w-8" data-id="element-749" />,
  title: 'Clothing & Fashion Access',
  description: 'The availability of affordable, stylish, and functional clothing across the full size spectrum.',
  examples: ['Mainstream brands stopping at size 24–28', 'Plus-size clothing costing significantly more', 'Limited options for professional and formal wear', 'Adaptive clothing rarely available in larger sizes'],
  color: 'border-brand-orange'
}, {
  icon: <Monitor className="h-8 w-8" data-id="element-750" />,
  title: 'Media & Cultural Representation',
  description: 'Visibility and portrayal of fat people in media, advertising, art, and culture — and whether that representation is dignified or dehumanising.',
  examples: ['Fat characters reduced to comic relief or cautionary tales', 'Stock photography excluding larger bodies', 'Advertising using fatness as a "before" image', 'Fat joy and complexity rarely depicted'],
  color: 'border-brand-magenta'
}, {
  icon: <Bus className="h-8 w-8" data-id="element-751" />,
  title: 'Transport & Mobility',
  description: 'The ability to use public and private transport systems — from airline seats to bus aisles to seatbelts — without humiliation or exclusion.',
  examples: ['Airline seats designed for a narrow range of bodies', 'Seatbelt extenders treated as shameful', 'Bus and train seating with fixed armrests', 'Ride-share vehicles too small for some passengers'],
  color: 'border-brand-cyan'
}, {
  icon: <GraduationCap className="h-8 w-8" data-id="element-752" />,
  title: 'Education & Learning Access',
  description: 'The ability to participate fully in educational settings — from classroom furniture to curriculum content to how fat students are treated.',
  examples: ["School desks that don't fit larger students", 'BMI screening and "health" curricula that shame fat children', 'PE environments hostile to fat bodies', 'Anti-fat bullying inadequately addressed'],
  color: 'border-brand-lime'
}, {
  icon: <Heart className="h-8 w-8" data-id="element-753" />,
  title: 'Social & Relational Access',
  description: 'The ability to form relationships, participate in social life, and exist in public without stigma, harassment, or dehumanisation.',
  examples: ['Dating and desirability politics', 'Public harassment and unsolicited comments', 'Social exclusion from activities and events', 'Fetishisation as the only form of "acceptance"'],
  color: 'border-brand-orange'
}];
const references = [{
  title: 'Fearing the Black Body: The Racial Origins of Fat Phobia',
  author: 'Sabrina Strings (2019)'
}, {
  title: 'Belly of the Beast: The Politics of Anti-Fatness as Anti-Blackness',
  author: "Da'Shaun L. Harrison (2021)"
}, {
  title: "What We Don't Talk About When We Talk About Fat",
  author: 'Aubrey Gordon (2020)'
}, {
  title: 'Fat Activism: A Radical Social Movement',
  author: 'Charlotte Cooper (2016)'
}, {
  title: 'The (Political) Economy of Fat',
  author: 'Various contributors, Fat Studies journal'
}];
export function CategoriesOfFatAccess() {
  return <div className="min-h-screen bg-brand-cream" data-id="element-754">
      <HeroSection title="Categories of Fat Access" subtitle="A framework for understanding the domains where fat people face structural barriers." tag="Fat Liberation & Justice" bgColor="magenta" data-id="element-755" />

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-756">
        <p className="text-xl text-gray-700 leading-relaxed mb-6" data-id="element-757">
          Fat oppression doesn't operate in a single dimension. It manifests
          across{' '}
          <strong className="text-brand-magenta" data-id="element-758">
            multiple, interconnected domains
          </strong>{' '}
          — from the physical design of spaces to the cultural stories told
          about fat bodies.
        </p>
        <p className="text-lg text-gray-600 leading-relaxed mb-6" data-id="element-759">
          This framework, informed by the work of fat liberation activists and
          scholars, identifies the key categories where fat people —
          particularly those at the larger end of the spectrum — face systemic
          barriers to full participation in society.
        </p>
        <div className="bg-white border-l-4 border-brand-magenta p-6" data-id="element-760">
          <p className="text-base text-gray-700 italic" data-id="element-761">
            Understanding these categories helps us move beyond individual
            prejudice and see anti-fatness as a structural issue — one that
            requires structural solutions.
          </p>
        </div>
      </section>

      {/* Levels of Access */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-762">
        <h2 className="text-3xl font-black uppercase mb-4 text-brand-black" data-id="element-763">
          The Five Levels of Fat Access
        </h2>
        <p className="text-gray-600 mb-10 max-w-3xl" data-id="element-764">
          Across every category, the degree of access fat people experience can
          be understood through five levels — from total inaccessibility to full
          systemic inclusion.
        </p>

        <div className="space-y-0" data-id="element-765">
          {[{
          level: 1,
          label: 'Inaccessibility',
          description: 'Complete exclusion. The space, service, or system was not designed with fat bodies in mind and offers no accommodation. Participation is impossible or requires extraordinary effort.',
          color: 'bg-red-600',
          barWidth: 'w-[10%]'
        }, {
          level: 2,
          label: 'Restricted Access',
          description: 'Access exists in theory but is heavily gatekept. Fat people may be able to participate only through workarounds, special requests, or by enduring humiliation.',
          color: 'bg-brand-magenta',
          barWidth: 'w-[30%]'
        }, {
          level: 3,
          label: 'Limited Access',
          description: 'Some provision is made, but it is inconsistent, inadequate, or tokenistic. Fat people can participate but face regular friction and exclusion at the margins.',
          color: 'bg-brand-orange',
          barWidth: 'w-[50%]'
        }, {
          level: 4,
          label: 'Basic Access',
          description: 'Reasonable accommodation exists. Fat people can participate with relative dignity, though gaps remain — particularly for those at the larger end of the spectrum.',
          color: 'bg-brand-cyan',
          barWidth: 'w-[75%]'
        }, {
          level: 5,
          label: 'Systemic Access',
          description: 'Full, structural inclusion. The space, service, or system was designed from the outset to accommodate the full diversity of body sizes. No special request or workaround is needed.',
          color: 'bg-brand-lime',
          barWidth: 'w-full'
        }].map((item, index) => <motion.div key={item.level} initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.08
        }} className="bg-white border-2 border-brand-black -mt-[2px] first:mt-0" data-id="element-766">
              <div className="flex flex-col sm:flex-row" data-id="element-767">
                {/* Level indicator */}
                <div className={`${item.color} w-full sm:w-20 flex items-center justify-center py-3 sm:py-0 flex-shrink-0`} data-id="element-768">
                  <span className="text-white font-black text-2xl" data-id="element-769">
                    L{item.level}
                  </span>
                </div>

                {/* Content */}
                <div className="p-6 flex-grow" data-id="element-770">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4" data-id="element-771">
                    <div className="flex-grow" data-id="element-772">
                      <h3 className="text-lg font-black uppercase text-brand-black mb-2" data-id="element-773">
                        {item.label}
                      </h3>
                      <p className="text-sm text-gray-700 leading-relaxed" data-id="element-774">
                        {item.description}
                      </p>
                    </div>
                  </div>
                  {/* Access bar */}
                  <div className="mt-4 h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-775">
                    <div className={`h-full ${item.color} ${item.barWidth} rounded-full transition-all`} data-id="element-776" />
                  </div>
                </div>
              </div>
            </motion.div>)}
        </div>

        <div className="flex items-center justify-between mt-4 px-2 text-xs font-bold uppercase text-gray-500" data-id="element-777">
          <span data-id="element-778">Total exclusion</span>
          <div className="flex-1 mx-4 h-px bg-gradient-to-r from-red-600 via-brand-orange to-brand-lime" data-id="element-779"></div>
          <span data-id="element-780">Full inclusion</span>
        </div>
      </section>

      {/* Categories Grid */}
      <section className="py-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-781">
        <h2 className="text-3xl font-black uppercase mb-4 text-brand-black" data-id="element-782">
          The Eight Categories
        </h2>
        <p className="text-gray-600 mb-12 max-w-3xl" data-id="element-783">
          Each category represents a domain of life where fat people face
          barriers that scale with body size.
        </p>

        <div className="space-y-8" data-id="element-784">
          {categories.map((category, index) => <motion.div key={category.title} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.05
        }} className={`bg-white border-2 border-brand-black border-l-8 ${category.color} overflow-hidden`} data-id="element-785">
              <div className="grid grid-cols-1 lg:grid-cols-3" data-id="element-786">
                <div className="p-8 lg:border-r border-gray-100" data-id="element-787">
                  <div className="text-brand-magenta mb-4" data-id="element-788">{category.icon}</div>
                  <h3 className="text-xl font-black uppercase text-brand-black mb-3 leading-tight" data-id="element-789">
                    {category.title}
                  </h3>
                  <p className="text-gray-700 text-sm leading-relaxed" data-id="element-790">
                    {category.description}
                  </p>
                </div>
                <div className="p-8 lg:col-span-2 bg-gray-50/50" data-id="element-791">
                  <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-4" data-id="element-792">
                    Examples of Barriers
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-793">
                    {category.examples.map((example, i) => <div key={i} className="flex items-start gap-2" data-id="element-794">
                        <span className="text-brand-magenta mt-1 flex-shrink-0" data-id="element-795">
                          →
                        </span>
                        <p className="text-sm text-gray-700" data-id="element-796">{example}</p>
                      </div>)}
                  </div>
                </div>
              </div>
            </motion.div>)}
        </div>
      </section>

      {/* Intersections */}
      <section className="py-20 bg-brand-navy text-white" data-id="element-797">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-798">
          <h2 className="text-3xl font-black uppercase mb-8" data-id="element-799">
            How Categories Intersect
          </h2>
          <p className="text-lg text-gray-200 mb-8 leading-relaxed" data-id="element-800">
            These categories don't exist in isolation. A fat person denied
            healthcare may also face employment discrimination, lack access to
            appropriate clothing for job interviews, and be unable to use public
            transport comfortably. The barriers compound.
          </p>
          <p className="text-lg text-gray-200 mb-8 leading-relaxed" data-id="element-801">
            When race, disability, gender, and class intersect with fatness, the
            barriers in each category intensify. A fat disabled Black woman
            navigates compounded exclusion across every single domain.
          </p>
          <div className="bg-white/10 p-6 border border-white/20" data-id="element-802">
            <p className="text-brand-magenta font-bold mb-2" data-id="element-803">Key principle:</p>
            <p className="text-gray-200" data-id="element-804">
              Fat liberation work must address all categories simultaneously.
              Improving clothing access while ignoring healthcare discrimination
              is not liberation — it's market expansion.
            </p>
          </div>
        </div>
      </section>

      {/* Critique */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-805">
        <div className="bg-white border-2 border-brand-black p-8" data-id="element-806">
          <div className="flex items-start gap-4" data-id="element-807">
            <AlertTriangle className="h-8 w-8 text-brand-magenta flex-shrink-0 mt-1" data-id="element-808" />
            <div data-id="element-809">
              <h3 className="text-xl font-black uppercase mb-4 text-brand-black" data-id="element-810">
                Critique & Limitations
              </h3>
              <div className="space-y-4 text-gray-700 text-sm leading-relaxed" data-id="element-811">
                <p data-id="element-812">
                  Any framework that categorises experience risks
                  oversimplification. These categories are{' '}
                  <strong data-id="element-813">analytical tools</strong>, not exhaustive descriptions
                  of lived reality.
                </p>
                <p data-id="element-814">Some critiques to hold alongside this framework:</p>
                <ul className="space-y-2 ml-4" data-id="element-815">
                  <li className="flex items-start" data-id="element-816">
                    <span className="text-brand-magenta mr-2" data-id="element-817">•</span>
                    Categories may not capture the full range of barriers in
                    non-Western contexts, where fatness carries different
                    cultural meanings.
                  </li>
                  <li className="flex items-start" data-id="element-818">
                    <span className="text-brand-magenta mr-2" data-id="element-819">•</span>
                    The framework centres access and barriers but may
                    underemphasise fat joy, community, and resistance.
                  </li>
                  <li className="flex items-start" data-id="element-820">
                    <span className="text-brand-magenta mr-2" data-id="element-821">•</span>
                    Categorisation can imply that barriers are discrete when in
                    practice they are deeply entangled.
                  </li>
                  <li className="flex items-start" data-id="element-822">
                    <span className="text-brand-magenta mr-2" data-id="element-823">•</span>
                    Frameworks developed primarily in the US/UK may not
                    translate directly to other cultural and economic contexts.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* References */}
      <section className="py-16 bg-white" data-id="element-824">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-825">
          <h2 className="text-2xl font-black uppercase mb-8 text-brand-black" data-id="element-826">
            References & Further Reading
          </h2>
          <div className="space-y-4" data-id="element-827">
            {references.map((ref, index) => <div key={index} className="flex items-start gap-3 py-3 border-b border-gray-100 last:border-b-0" data-id="element-828">
                <BookOpen className="h-4 w-4 text-brand-magenta flex-shrink-0 mt-1" data-id="element-829" />
                <div data-id="element-830">
                  <p className="font-bold text-brand-black text-sm" data-id="element-831">
                    {ref.title}
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-832">{ref.author}</p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Related Pages */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-833">
        <h2 className="text-2xl font-black uppercase mb-8 text-brand-black" data-id="element-834">
          Related Frameworks
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-835">
          <Link to="/clusters/fat-frameworks" className="group block" data-id="element-836">
            <div className="bg-white border-2 border-brand-black p-6 hover:shadow-lg transition-shadow flex items-center justify-between" data-id="element-837">
              <div data-id="element-838">
                <h3 className="font-black uppercase text-brand-black group-hover:text-brand-magenta transition-colors" data-id="element-839">
                  The Fat Spectrum & Size Privilege
                </h3>
                <p className="text-sm text-gray-600 mt-1" data-id="element-840">
                  Smallfat to infinifat — how size affects access.
                </p>
              </div>
              <ArrowRight className="h-5 w-5 text-brand-magenta group-hover:translate-x-1 transition-transform flex-shrink-0" data-id="element-841" />
            </div>
          </Link>
          <Link to="/clusters/fat-theory-catalogue" className="group block" data-id="element-842">
            <div className="bg-white border-2 border-brand-black p-6 hover:shadow-lg transition-shadow flex items-center justify-between" data-id="element-843">
              <div data-id="element-844">
                <h3 className="font-black uppercase text-brand-black group-hover:text-brand-magenta transition-colors" data-id="element-845">
                  Fat Theory Catalogue
                </h3>
                <p className="text-sm text-gray-600 mt-1" data-id="element-846">
                  Browse all frameworks and theories.
                </p>
              </div>
              <ArrowRight className="h-5 w-5 text-brand-magenta group-hover:translate-x-1 transition-transform flex-shrink-0" data-id="element-847" />
            </div>
          </Link>
        </div>
      </section>
    </div>;
}